import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  collection,
  getCountFromServer,
  getDocs,
  query,
  where,
  doc,
  orderBy,
  getDocFromServer,
} from "firebase/firestore";
import { auth, db } from "./../../firebase-config";
import { onAuthStateChanged } from "firebase/auth";
import { Helmet } from "react-helmet";
import { Avatar, Badge, Group, Loader, Paper, Table, Title, Text, Container } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import styles from "./dashboard.module.css";
import { StatsGrid, StampsTable } from "../../components";
import { AppContext } from "../../App";
import {
  formatDateInNepalTimezone,
  getNepalDateDaysAgoFromDateString,
  getNepalDateString,
  getNepalDayStartFromDateString,
  getNepalNextDayStartFromDateString,
  getNepalPreviousDayStartFromDateString,
  normalizeNepalDateString,
} from "../../utils/timezone";

// user data
// {
//   "phone": "+977 9808201347",
//   "birthday": {
//       "seconds": 498940200,
//       "nanoseconds": 0
//   },
//   "name": "sharav",
//   "gender": "Male",
//   "fcmToken": "e1m6FQ0nQOmLJQhbhj37ed:APA91bFWe8a1HQNOX_KYy3vlxkS-NgFXkMKnXscSYewePgXmeqA3mRMBU8o-2owjWvNfzoIbWhLBiRUqxqwuTjytgf-KtrYkQaiBLNobZR3FidiEfnRmNhiayugun6SS93FiNdYa1sEj",
//   "image": ""
// }

// stamp data
// {
//   "claimed": false,
//   "stampedDate": {
//       "seconds": 1718421665,
//       "nanoseconds": 532000000
//   },
//   "stampedBy": "hUvYPhbVlINmacAXyaNmTXVHR3Y2",
//   "brandId": "RbFSquUg4KEXLIyUgFAY",
//   "stampCardId": "oKe4BFlc6s9itcMWejss",
//   "userId": "3KrWw7LcfAfsmfIH0dxQPts56XE3",
//   "outletId": "RJc9OMEiAmoetZhsc4v2",
//   "expiryDate": {
//       "seconds": 1718421780,
//       "nanoseconds": 0
//   }
// }

export const Dashboard = () => {
  const { globalState } = useContext(AppContext);
  const todayNepalDateString = useMemo(() => getNepalDateString(), []);
  const [selectedDate, setSelectedDate] = useState(todayNepalDateString);
  const toPickerDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  };

  const [counts, setCounts] = useState({
    brands: 0,
    stamps: 0,
    users: 0,
    rewards: 0,
    activeToday: 0,
    active24Hours: 0,
    active30Days: 0,
    newUsers: 0,
  });

  const [todayCounts, setTodayCounts] = useState({
    stamps: 0,
    rewards: 0,
  });

  const [activeBrandsToday, setActiveBrandsToday] = useState([]);
  const [latestStamps, setLatestStamps] = useState([]);
  const [allUsers, setBrandSpecificUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const selectedDateValue = useMemo(() => toPickerDate(selectedDate), [selectedDate]);
  const maxPickerDate = useMemo(
    () => toPickerDate(todayNepalDateString),
    [todayNepalDateString]
  );

  const {
    selectedDayStart,
    selectedNextDayStart,
    selectedPreviousDayStart,
    selectedDayThirtyDaysAgo,
    selectedDayLabel,
    previousDayLabel,
  } = useMemo(() => {
    const normalizedSelectedDate = normalizeNepalDateString(
      selectedDate,
      todayNepalDateString
    );
    const dayStart = getNepalDayStartFromDateString(normalizedSelectedDate);
    const nextDayStart = getNepalNextDayStartFromDateString(normalizedSelectedDate);
    const previousDayStart = getNepalPreviousDayStartFromDateString(normalizedSelectedDate);
    const dayThirtyDaysAgo = getNepalDateDaysAgoFromDateString(30, normalizedSelectedDate);

    return {
      selectedDayStart: dayStart,
      selectedNextDayStart: nextDayStart,
      selectedPreviousDayStart: previousDayStart,
      selectedDayThirtyDaysAgo: dayThirtyDaysAgo,
      selectedDayLabel: formatDateInNepalTimezone(dayStart, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      previousDayLabel: formatDateInNepalTimezone(previousDayStart, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    };
  }, [selectedDate, todayNepalDateString]);

  const handleSelectedDateChange = (value) => {
    if (!value) {
      setSelectedDate(todayNepalDateString);
      return;
    }

    const nextValue = [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, "0"),
      String(value.getDate()).padStart(2, "0"),
    ].join("-");
    const normalizedDate = normalizeNepalDateString(nextValue, selectedDate);
    setSelectedDate(
      normalizedDate > todayNepalDateString ? todayNepalDateString : normalizedDate
    );
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        window.location.href = "/admin";
      }
    });

    // Clean up the subscription
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const countsData = {};
        const collections = ["brands", "stamps", "users", "rewards"];

        if (
          globalState.currentBrand !== "All" &&
          globalState.currentBrand !== undefined
        ) {
          console.log("fetching data for brand", globalState.currentBrand);

          const fetchCollectionCount = async (coll, fieldName, value) => {
            const q = query(
              collection(db, coll),
              where(fieldName, "==", value)
            );
            const querySnapshot = await getCountFromServer(q);
            return querySnapshot.data().count;
          };

          const fetchBrandUserCount = async (coll) => {
            const q = query(
              collection(db, coll, globalState.currentBrand, "clients")
            );
            const querySnapshot = await getCountFromServer(q);
            return querySnapshot.data().count;
          };

          await Promise.all(
            collections.map(async (coll) => {
              let count;
              if (coll === "users") {
                count = await fetchBrandUserCount("brands");
              } else {
                count = await fetchCollectionCount(
                  coll,
                  "brandId",
                  globalState.currentBrand
                );
              }
              countsData[coll] = count;
            })
          );
        } else {
          console.log("fetching data for all brands");
          await Promise.all(
            collections.map(async (coll) => {
              const querySnapshot = await getCountFromServer(
                collection(db, coll)
              );
              countsData[coll] = querySnapshot.data().count;
            })
          );

          await Promise.all([
            (async () => {
              const nepalToday = selectedDayStart;
              const nepalTomorrow = selectedNextDayStart;
              const last30Days = selectedDayThirtyDaysAgo;
              const q30 = query(
                collection(db, "users"),
                where("lastOpened", ">=", last30Days),
                where("lastOpened", "<", nepalToday)
              );
              const querySnapshot30 = await getCountFromServer(q30);
              countsData.active30Days = querySnapshot30.data().count;
              console.log("active 30 days", countsData.active30Days);


              //active 24 hours
              const yesterday = selectedPreviousDayStart;
              const q24 = query(
                collection(db, "users"),
                where("lastOpened", ">=", yesterday),
                where("lastOpened", "<", nepalToday)
              );
              const querySnapshot24 = await getCountFromServer(q24);
              countsData.active24Hours = querySnapshot24.data().count;
              console.log("active 24 hours", countsData.active24Hours);

              // active today
              const q = query(
                collection(db, "users"),
                where("lastOpened", ">=", nepalToday),
                where("lastOpened", "<", nepalTomorrow)
              );

              const querySnapshot = await getCountFromServer(q);
              countsData.activeToday = querySnapshot.data().count;
              console.log("active today", countsData.activeToday);

              // new users
              const qNewUsers = query(
                collection(db, "users"),
                where("createdAt", ">=", nepalToday),
                where("createdAt", "<", nepalTomorrow),
              );
              const querySnapshotNewUsers = await getCountFromServer(qNewUsers);
              countsData.newUsers = querySnapshotNewUsers.data().count;
              console.log("new users", countsData.newUsers);

            })(),
          ]);
        }
        setCounts(countsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [globalState.currentBrand, selectedDayStart, selectedNextDayStart, selectedPreviousDayStart, selectedDayThirtyDaysAgo]);

  useEffect(() => {
    setLoading(true);
    let todayCountsLocal = {};
    let yesterdayCounts = {};

    const fetchTodayData = async () => {
      try {
        const nepalToday = selectedDayStart;
        const nepalTomorrow = selectedNextDayStart;

        const fetchCollectionCount = async (coll, fieldName) => {
          // const q = query(collection(db, coll), where(fieldName, '>=', today), where(fieldName, '<', tomorrow));
          var q;
          if (
            globalState.currentBrand !== "All" &&
            globalState.currentBrand !== undefined
          ) {
            q = query(
              collection(db, coll),
              where(fieldName, ">=", nepalToday),
              where(fieldName, "<", nepalTomorrow),
              where("brandId", "==", globalState.currentBrand)
            );
          } else {
            q = query(
              collection(db, coll),
              where(fieldName, ">=", nepalToday),
              where(fieldName, "<", nepalTomorrow)
            );
          }
          const querySnapshot = await getCountFromServer(q);
          return querySnapshot.data().count;
        };

        const [stampsCount, rewardsCount] = await Promise.all([
          fetchCollectionCount("stamps", "stampedDate"),
          fetchCollectionCount("rewards", "claimedDate"),
        ]);

        todayCountsLocal = {
          stamps: stampsCount,
          rewards: rewardsCount,
        };
        setTodayCounts(todayCountsLocal);
        setLoading(false);
        await fetchYesterdayData();
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const fetchYesterdayData = async () => {
      setLoading(true);
      try {
        const nepalToday = selectedDayStart;
        const nepalYesterday = selectedPreviousDayStart;

        const fetchCollectionCount = async (coll, fieldName) => {
          // const q = query(collection(db, coll), where(fieldName, '>=', yesterday), where(fieldName, '<', today));
          var q;
          if (
            globalState.currentBrand !== "All" &&
            globalState.currentBrand !== undefined
          ) {
            q = query(
              collection(db, coll),
              where(fieldName, ">=", nepalYesterday),
              where(fieldName, "<", nepalToday),
              where("brandId", "==", globalState.currentBrand)
            );
          } else {
            q = query(
              collection(db, coll),
              where(fieldName, ">=", nepalYesterday),
              where(fieldName, "<", nepalToday)
            );
          }
          const querySnapshot = await getCountFromServer(q);
          return querySnapshot.data().count;
        };

        const [stampsCount, rewardsCount] = await Promise.all([
          fetchCollectionCount("stamps", "stampedDate"),
          fetchCollectionCount("rewards", "claimedDate"),
        ]);

        yesterdayCounts = {
          stamps: stampsCount,
          rewards: rewardsCount,
        };

        calculatePercentageDifference();
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const calculatePercentageDifference = () => {
      const stampsDiff =
        yesterdayCounts.stamps !== 0
          ? ((todayCountsLocal.stamps - yesterdayCounts.stamps) /
            yesterdayCounts.stamps) *
          100
          : 0;
      const rewardsDiff =
        yesterdayCounts.rewards !== 0
          ? ((todayCountsLocal.rewards - yesterdayCounts.rewards) /
            yesterdayCounts.rewards) *
          100
          : 0;

      setTodayCounts((prevCounts) => ({
        ...prevCounts,
        stampsDiff: stampsDiff.toFixed(2),
        rewardsDiff: rewardsDiff.toFixed(2),
      }));
    };

    fetchTodayData();
  }, [globalState.currentBrand, selectedDayStart, selectedNextDayStart, selectedPreviousDayStart]);

  useEffect(() => {
    const fetchLatestData = async () => {
      setLoading(true);
      try {
        const nepalToday = selectedDayStart;
        const nepalTomorrow = selectedNextDayStart;

        // Create base query
        let baseQuery = query(
          collection(db, "stamps"),
          orderBy("stampedDate", "desc"),
          where("stampedDate", ">=", nepalToday),
          where("stampedDate", "<", nepalTomorrow)
        );

        // Add brand filter if specific brand is selected
        if (globalState.currentBrand && globalState.currentBrand !== "All") {
          baseQuery = query(
            collection(db, "stamps"),
            orderBy("stampedDate", "desc"),
            where("stampedDate", ">=", nepalToday),
            where("stampedDate", "<", nepalTomorrow),
            where("brandId", "==", globalState.currentBrand)
          );
        }

        const querySnapshot = await getDocs(baseQuery);
        const stamps = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        // Fetch users and brands data
        const userIds = Array.from(new Set(stamps.map((stamp) => stamp.userId)));
        const brandIds = Array.from(new Set(stamps.map((stamp) => stamp.brandId)));

        const [users, brands] = await Promise.all([
          Promise.all(
            userIds.map(async (userId) => {
              const userDoc = await getDocFromServer(doc(db, "users", userId));
              return { ...userDoc.data(), id: userId };
            })
          ),
          Promise.all(
            brandIds.map(async (brandId) => {
              const brandDoc = await getDocFromServer(doc(db, "brands", brandId));
              return { ...brandDoc.data(), id: brandId };
            })
          ),
        ]);

        const extractBaseId = (id) => {
          for (let i = id.length - 1; i >= 0; i--) {
            if (!/\d/.test(id[i])) {
              return id.substring(0, i + 1);
            }
          }

          return id;
        };

        const usersMap = new Map(users.map((user) => [user.id, user]));
        const brandsMap = new Map(brands.map((brand) => [brand.id, brand]));

        const stampsMap = new Map();

        for (const stamp of stamps) {
          const baseId = extractBaseId(stamp.id);

          const stampActivityStamp = {
            id: stamp.id,
            isClaimed: stamp.stamps?.[0]?.isClaimed ?? false,
          };

          if (stampsMap.has(baseId)) {
            const existingStamp = stampsMap.get(baseId);

            existingStamp.stamps.push(stampActivityStamp);
            existingStamp.count = existingStamp.stamps.length;
          } else {
            stampsMap.set(baseId, {
              ...stamp,
              id: baseId,
              stamps: [stampActivityStamp],
              count: 1,
              user: usersMap.get(stamp.userId),
              brand: brandsMap.get(stamp.brandId),
            });
          }
        }

        const stampsData = Array.from(stampsMap.values());

        let rewardsQuery = query(
          collection(db, "rewards"),
          where("claimedDate", ">=", nepalToday),
          where("claimedDate", "<", nepalTomorrow)
        );

        if (globalState.currentBrand && globalState.currentBrand !== "All") {
          rewardsQuery = query(
            collection(db, "rewards"),
            where("claimedDate", ">=", nepalToday),
            where("claimedDate", "<", nepalTomorrow),
            where("brandId", "==", globalState.currentBrand)
          );
        }

        const rewardsSnapshot = await getDocs(rewardsQuery);
        const rewardsByBrand = rewardsSnapshot.docs.reduce((acc, rewardDoc) => {
          const reward = rewardDoc.data();
          if (!reward.brandId) return acc;

          acc.set(reward.brandId, (acc.get(reward.brandId) || 0) + 1);
          return acc;
        }, new Map());

        const activeBrandsMap = stampsData.reduce((acc, stampRow) => {
          if (!stampRow.brandId) return acc;

          if (!acc.has(stampRow.brandId)) {
            acc.set(stampRow.brandId, {
              brandId: stampRow.brandId,
              brand: stampRow.brand,
              users: new Set(),
              stampsCount: 0,
              rewardsCount: rewardsByBrand.get(stampRow.brandId) || 0
            });
          }

          const brandActivity = acc.get(stampRow.brandId);
          if (stampRow.userId) {
            brandActivity.users.add(stampRow.userId);
          }
          brandActivity.stampsCount += stampRow.count || 1;

          return acc;
        }, new Map());

        const activeBrandsData = Array.from(activeBrandsMap.values())
          .map((brandActivity) => ({
            ...brandActivity,
            usersCount: brandActivity.users.size
          }))
          .sort((a, b) => {
            if (b.stampsCount !== a.stampsCount) {
              return b.stampsCount - a.stampsCount;
            }

            return (a.brand?.name || "").localeCompare(b.brand?.name || "");
          });

        setActiveBrandsToday(activeBrandsData);
        setLatestStamps(stampsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if globalState.currentBrand is defined
    if (globalState.currentBrand !== undefined) {
      fetchLatestData();
    }
  }, [globalState.currentBrand, selectedDayStart, selectedNextDayStart]);

  const activeBrandRows = activeBrandsToday.map((brandActivity) => (
    <Table.Tr key={brandActivity.brandId}>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <Avatar src={brandActivity.brand?.logo} size={34} radius={6} />
          <Text fw={600} fz="sm">{brandActivity.brand?.name || "Unknown brand"}</Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="blue">{brandActivity.usersCount}</Badge>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="yellow">{brandActivity.stampsCount}</Badge>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="green">{brandActivity.rewardsCount}</Badge>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Helmet>
        <title>Lollet Admin - Dashboard</title>
      </Helmet>
      <Container size="xxl" className={styles.container}>
        {error && (
          <Text color="red" align="center">
            {error}
          </Text>
        )}
        {loading ? (
          <Loader />
        ) : (
          <>
            <Group justify="space-between" align="end" mt="md" mb="lg">
              <div>
                <Title order={2}>Dashboard</Title>
                <Text c="dimmed" size="sm">
                  Daily stats for {selectedDayLabel} in Nepal time
                </Text>
              </div>
              <DatePickerInput
                label="Stats date"
                value={selectedDateValue}
                onChange={handleSelectedDateChange}
                maxDate={maxPickerDate}
                valueFormat="MMM D, YYYY"
                allowDeselect={false}
                className={styles.datePicker}
              />
            </Group>
            <StatsGrid
              data={[
                globalState.currentBrand !== "All" &&
                  globalState.currentBrand !== undefined
                  ? {
                    title: "Brand",
                    value: globalState.brands.find(
                      (brand) => brand.id === globalState.currentBrand
                    )?.name,
                    smallText: true,
                    icon: "brands",
                    today: -1,
                    diff: -1,
                  }
                  : {
                    title: "Brands",
                    value: counts.brands,
                    icon: "brands",
                    today: -1,
                    diff: -1,
                  },

                globalState.currentBrand !== "All" &&
                  globalState.currentBrand !== undefined &&
                  allUsers.length > 0
                  ? {
                    title: "Users",
                    value: allUsers.length,
                    redirect: "/admin/users",
                    icon: "users",
                  }
                  : {
                    title: "Users",
                    value: counts.users,
                    redirect: "/admin/users",
                    icon: "users",
                    today: counts.newUsers,
                    todayLabel: selectedDayLabel,
                    diff: counts.active30Days,
                    diffText: "(" + (counts.users && counts.active30Days
                      ? ((counts.active30Days / counts.users) * 100).toFixed(1) + "%"
                      : "0%") + ") active in the last 30 days",
                  },
                {
                  title: "Stamps",
                  value: counts.stamps,
                  today: todayCounts.stamps,
                  todayLabel: selectedDayLabel,
                  diff: todayCounts.stampsDiff,
                  diffLabel: previousDayLabel,
                  icon: "stamps",
                },
                {
                  title: "Rewards",
                  value: counts.rewards,
                  today: todayCounts.rewards,
                  todayLabel: selectedDayLabel,
                  diff: todayCounts.rewardsDiff,
                  diffLabel: previousDayLabel,
                  icon: "rewards",
                },
                {
                  title: "Active Users",
                  value: counts.activeToday,
                  today: counts.active24Hours,
                  todayLabel: previousDayLabel,
                  diff: -1,
                  icon: "users",
                },
              ]}
            />
            {/* {globalState.currentBrand === 'All' && (
              <Center>
                <Button variant="transparent" onClick={
                  () => {
                    window.location.href = '/admin/dashboard/brands';
                  }
                } align="center">View more Detail</Button>
              </Center>
            )} */}
            <Title order={2} align="center" mt="lg">
              Active Brands on {selectedDayLabel}
            </Title>
            <Paper withBorder radius="md" p="md" mt="md">
              <Table.ScrollContainer minWidth={700}>
                <Table verticalSpacing="sm" striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Brand</Table.Th>
                      <Table.Th>Users</Table.Th>
                      <Table.Th>Stamps</Table.Th>
                      <Table.Th>Rewards</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {activeBrandRows.length > 0 ? activeBrandRows : (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Text c="dimmed" ta="center" py="lg">
                            No active brands on {selectedDayLabel}.
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>
            <Title order={2} align="center" mt="lg">
              Stamps on {selectedDayLabel}
            </Title>
            <StampsTable data={latestStamps} loading={loading} error={error} />
          </>
        )}
      </Container>
    </>
  );
};
